import { Nullable } from '@cast/leadcast-types';
import { Type } from 'class-transformer';
import { optionalCustomObject, yesNoUnknown } from 'components/shared/CASTForm/validators';
import { Inspection, ServiceLineInspection } from 'types/models/Inspection';
import { InspectionOptimizerOutput } from 'types/models/inspections/InspectionOptimizerOutput';
import { Material } from 'types/models/inventory/Material';
import { ServiceLineMaterial } from 'types/models/ServiceLineMaterial';
import { byWorstConnectorMaterial, byWorstMaterial } from 'utils/helpers';
import { z } from 'zod';
import { Geometry } from '../Geometry';
import { OnPremisePlumbing } from '../PremisePlumbing';
import PropertyRightOfEntryForm from '../PropertyRightOfEntryForm';
import RightOfEntryStatus from '../RightOfEntryStatus';
import { ServiceLine, ServiceLineSide } from '../ServiceLine';
import { CustomProperties } from './CustomProperties';

interface PropertyROE {
  id: PropertyRightOfEntryForm['id'];
  propertyId: PropertyRightOfEntryForm['property_id'];
  customerName: PropertyRightOfEntryForm['customer_name'];
  customerIsHomeowner: PropertyRightOfEntryForm['customer_is_homeowner'];
  customerPhoneNumber: PropertyRightOfEntryForm['customer_phone_number'];
  customerEmail: PropertyRightOfEntryForm['customer_email'];
  submittedBy: PropertyRightOfEntryForm['submitted_by'];
  submittedOn: PropertyRightOfEntryForm['submitted_on'];
  fileId: PropertyRightOfEntryForm['file_id'];
  hashedContent: PropertyRightOfEntryForm['hashed_content'];
  status: RightOfEntryStatus['code'];
  method: PropertyRightOfEntryForm['method'];
  statusUpdatedOn: PropertyRightOfEntryForm['status_updated_on'];
  lastReviewedBy: PropertyRightOfEntryForm['reviewed_by'];
  notes: PropertyRightOfEntryForm['notes'];
  userResponse: RightOfEntryStatus['code'];
  userRespondedOn: Nullable<Date>;
  metadata: Nullable<{ origin: string }>;
}

class PropertyROE {}

export interface PropertyDetails {
  id: string;
  geometry: Nullable<Geometry>;
  accountId: Nullable<string>;
  externalId: Nullable<string>;
  parcelId: Nullable<string>;
  address: Nullable<string>;
  addressUnit: Nullable<string>;
  city: Nullable<string>;
  state: Nullable<string>;
  county: Nullable<string>;
  zipcode: Nullable<string>;
  yearBuilt: Nullable<number>;
  zoneName: string;
  pwsId: string;
  buildingType: string;
  registrationCode: Nullable<string>;
  startDate: Nullable<string>;
  endDate: Nullable<string>;
  onPremisePlumbing: Nullable<OnPremisePlumbing>;
  rightOfEntry: Nullable<PropertyROE>;
  serviceLines: ServiceLine[];
  inspections: Inspection[];
  inspectionOptimizerOutput: Nullable<InspectionOptimizerOutput>;
  properties: CustomProperties;
  name: Nullable<string>;
  sensitivePopulation: Nullable<boolean>;
  disadvantagedNeighborhood: Nullable<boolean>;
  geoidC: Nullable<string>;
  geoidBg: Nullable<string>;
  ownershipStatus: string;
  lifecycleStatusCode: Nullable<string>;
}

export class PropertyDetails {
  @Type(() => OnPremisePlumbing)
  onPremisePlumbing: Nullable<OnPremisePlumbing>;

  @Type(() => ServiceLine)
  serviceLines: ServiceLine[];

  @Type(() => PropertyROE)
  rightOfEntry: Nullable<PropertyROE>;

  @Type(() => InspectionOptimizerOutput)
  inspectionOptimizerOutput: Nullable<InspectionOptimizerOutput>;

  getAllServiceLineMaterials(side?: ServiceLineSide): ServiceLineMaterial[] {
    let allSls = this.serviceLines;
    if (side) {
      allSls = allSls.filter((sl) => sl.side === side);
    }
    return allSls.flatMap((sl) => {
      return sl.serviceLineMaterials;
    });
  }

  getAllActiveServiceLineMaterials(side?: ServiceLineSide): ServiceLineMaterial[] {
    return this.getAllServiceLineMaterials(side).filter((slm) => slm.isActive);
  }

  getAllInactiveServiceLineMaterials(side?: ServiceLineSide): ServiceLineMaterial[] {
    return this.getAllServiceLineMaterials(side).filter((slm) => slm.isActive === false);
  }

  getActiveServiceLines(side?: ServiceLineSide): ServiceLine[] {
    return this.serviceLines.filter((sl) => sl.isActive && (!side || sl.side === side));
  }

  getInspectedMaterialOnRecord(inspection: ServiceLineInspection): ServiceLineMaterial | undefined {
    const activeSlms = this.getAllActiveServiceLineMaterials(inspection.kind);
    const materialOnRecord = activeSlms.find(
      (slm) =>
        slm.id === inspection.serviceLineMaterialId &&
        slm.material.code === inspection.material.code
    );
    return materialOnRecord;
  }

  getWorstConnectorMaterial(): Material['code'] {
    const sls = this.getActiveServiceLines('Utility').sort(byWorstConnectorMaterial);
    if (sls.length > 0) {
      return sls[0].connectorMaterial?.code ?? 'UNK';
    } else {
      return 'UNK';
    }
  }

  getWorstMaterial(side?: ServiceLineSide): ServiceLineMaterial | null {
    const materials = this.getAllActiveServiceLineMaterials(side).sort(byWorstMaterial);
    return materials.length > 0 ? materials[0] : null;
  }
}

export const lifecycleStatusSchema = z.object({
  value: z.string().optional(),
  id: z.string().optional(),
  code: z.string().optional(),
  recordId: z.string().optional(),
});

export const serviceLineMaterialSchema = z.object({
  id: z.string().nonempty(),
  material: z.string().nonempty('Material is required'),
  method: z.string().nonempty('Method is required'),
  source: z.string().nullable(),
  verificationRecommended: z.boolean(),
});

export const serviceLineSchema = z.object({
  id: z.string().nonempty(),
  materials: z.array(serviceLineMaterialSchema),
  externalId: z.string().nonempty(),
  serviceLineType: z.string().nonempty('Please specify what type of service line this is'),
  installationDate: z.coerce
    .date({ invalid_type_error: 'Please enter a valid installation date' })
    .min(new Date('01/01/1600'), 'Please enter a valid installation date')
    .max(new Date(), 'Please enter a valid installation date')
    .nullish(),
  diameter: z.number().nullish(),
  tapSize: z.number().nullish(),
  length: z.preprocess((len) => (!len ? null : len), z.number().nullish()),
  waterMainDiameter: z.number().nullish(),
  contract: z.string().nullish(),
  isPreviouslyLead: yesNoUnknown,
  previousLeadSource: z.string().nullish(),
  containsLeadSolder: yesNoUnknown,
  hasOtherFittingsContainingLead: yesNoUnknown,
  connectorMaterial: z.string().nullish(),
  connectorSource: z.string().nullish(),
  comments: z.string().nullish(),
  properties: optionalCustomObject,
  lifecycleStatus: lifecycleStatusSchema,
});
